/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useHover, useFocus } from 'react-aria';

import usePageNavigation from './usePageNavigation';
import { usePage } from '../../api/use';

const SubSection = memo(({ id, top, height, prevId }) => {
	const { hiliteIdSub, scrollToItemById } = usePageNavigation();
	const [hover, setHover] = useState(false);
	const isCurrent = useMemo(() => hiliteIdSub === id, [hiliteIdSub, id]);
	const isNext = useMemo(() => (hiliteIdSub === prevId ? Math.random() : 0), [hiliteIdSub, prevId]);
	const { hoverProps } = useHover({
		onHoverStart: (e) => !isCurrent && setHover(Math.random()),
		onHoverEnd: (e) => setHover(false),
	});

	const { focusProps } = useFocus({
		onFocus: (e) => !isCurrent && setHover(Math.random()),
		onBlur: (e) => setHover(false),
	});

	useEffect(() => {
		if (isCurrent) setHover(false);
	}, [isCurrent, setHover]);

	const onClick = useCallback(() => {
		scrollToItemById(null, id);
	}, [scrollToItemById, id]);

	const buttonProps = useMemo(
		() =>
			isCurrent
				? {}
				: {
						role: 'button',
						tabIndex: 0,
						...hoverProps,
						...focusProps,
						onClick,
				  },
		[isCurrent, hoverProps, focusProps, onClick]
	);

	return (
		<>
			<rect
				x="0"
				y={top}
				width="18"
				height={height}
				fill="transparent"
				{...buttonProps}
				css={css`
					pointer-events: all;

					&[role='button'] {
						cursor: pointer;
					}

					&:focus-visible,
					&:focus {
						outline: none;
					}
				`}
			/>
			<motion.circle
				initial={{ r: 0 }}
				animate={{ r: isCurrent ? 8 : 1 }}
				cx={9}
				cy={top + 4 + height * 0.5}
				fill="currentColor"
			/>
			<AnimatePresence>
				{!!hover && (
					<motion.circle
						key={`hover${hover}`}
						initial={{ opacity: 0, scale: 0 }}
						exit={{
							opacity: 0,
							transition: {
								duration: 1,
							},
						}}
						animate={{
							scale: [0, 10],
							opacity: [1, 0],
							transition: {
								duration: 1,
							},
						}}
						r={2}
						cx="9"
						cy={top + 4 + height * 0.5}
					/>
				)}
			</AnimatePresence>

			<g transform={`translate(0,${top - 12 + height * 0.5})`}>
				<AnimatePresence>
					{!!isNext && (
						<motion.path
							key={isNext}
							initial={{ opacity: 0, y: 0, scale: 2 }}
							animate={{
								y: [0, 12],
								opacity: [0, 1, 0],
								transition: { repeat: Infinity, duration: 1 },
							}}
							exit={{ opacity: 0, y: 8, transition: { duration: 0.1 } }}
							d="M7 0L9 2L11 0"
							fill="none"
							strokeWidth={1}
							stroke="currentColor"
						/>
					)}
				</AnimatePresence>
			</g>
		</>
	);
});

const MainSection = memo(({ id, hash, label, top, height, allHeight, subItems, ...props }) => {
	return (
		<g>
			{!!subItems &&
				subItems.map((it, i) => {
					return (
						<SubSection
							key={it.id}
							id={it.id}
							top={(allHeight / 100) * it.top}
							height={(allHeight / 100) * it.height}
							prevId={it.prevId}
						/>
					);
				})}
		</g>
	);
});

const PageNavigation = () => {
	const { items, subItems, nextPrevIds, scrollToNext, scrollToPrev } = usePageNavigation();

	const {
		vitality: { loaded },
	} = usePage();

	const ref = useRef();

	const [height, setHeight] = useState(0);

	const enhancedItems = useMemo(() => {
		if (!items) return null;
		let prevSubId;

		return items.map((item) => {
			return {
				...item,
				children:
					!!subItems[item.id] && !!subItems[item.id].length
						? subItems[item.id].map((subItem, j) => {
								const ret = { ...subItem, prevId: prevSubId };
								prevSubId = subItem.id;
								return ret;
						  })
						: null,
			};
		});
	}, [items, subItems]);

	useEffect(() => {
		if (!ref.current) return;
		const onResize = () => {
			setHeight(ref.current.clientHeight);
		};
		onResize();
		window.addEventListener('resize', onResize);

		return () => window.removeEventListener('resize', onResize);
	}, [ref, setHeight]);

	return (
		<div
			ref={ref}
			css={(theme) => css`
				position: fixed;
				z-index: var(--zIndex-pageNav);
				right: ${theme.dimension.container.padding.s};
				top: 20%;
				bottom: 20%;
				pointer-events: none;

				@media (min-width: ${theme.breakpoint.m}) {
					right: ${theme.dimension.container.padding.m};
				}

				@media (min-width: ${theme.breakpoint.l}) {
					right: ${theme.dimension.container.padding.l};
				}
			`}
		>
			{!!loaded && !!enhancedItems && (
				<svg
					xmlns="http://www.w3.org/2000/svg"
					xmlSpace="preserve"
					viewBox={`0 0 18 ${height}`}
					width="18"
					height={height}
					css={css`
						display: block;
						overflow: visible;
						fill: currentColor;

						& * {
							vector-effect: non-scaling-stroke;
						}
					`}
				>
					<AnimatePresence>
						{!!nextPrevIds[0] && (
							<motion.g
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								css={css`
									pointer-events: all;
									cursor: pointer;
								`}
								onClick={scrollToPrev}
							>
								<circle cx="9" cy="-9" r="9" />
								<rect x="0" y="-9" width="18" height="9" />
								<path
									transform="translate(0,-7)"
									d="M5 0L9 -4L13 0"
									fill="none"
									strokeWidth={1}
									css={(theme) =>
										css`
											stroke: ${theme.color.bg};
										`
									}
								/>
							</motion.g>
						)}
					</AnimatePresence>
					{enhancedItems.map((item, i) => (
						<MainSection
							key={item.id}
							id={item.id}
							hash={item.hash}
							top={(height / 100) * item.top}
							height={(height / 100) * item.height}
							allHeight={height}
							label={item.label}
							subItems={item.children}
						/>
					))}
					<AnimatePresence>
						{!!nextPrevIds[1] && (
							<motion.g
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								css={css`
									pointer-events: all;
									cursor: pointer;
								`}
								onClick={scrollToNext}
							>
								<rect x="0" y={height} width="18" height="9" />
								<circle cx="9" cy={height + 9} r="9" />
								<path
									transform={`translate(0,${height + 7})`}
									d="M5 0L9 4L13 0"
									fill="none"
									strokeWidth={1}
									css={(theme) =>
										css`
											stroke: ${theme.color.bg};
										`
									}
								/>
							</motion.g>
						)}
					</AnimatePresence>
				</svg>
			)}
		</div>
	);
};

export default memo(PageNavigation);
